import { createFileRoute, Link } from '@tanstack/react-router';
import { type ReactNode, useState } from 'react';
import { BrandMark } from '../components/layout/BrandMark';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { authClient } from '../lib/auth-client';

export const Route = createFileRoute('/forgot-password')({
  component: ForgotPasswordPage,
});

function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [resetLink, setResetLink] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      // v1: better-auth returns a reset token — we display a copyable link
      // (no email service required for a 2-person household)
      const result = await authClient.requestPasswordReset({
        email,
        redirectTo: `${window.location.origin}/reset-password`,
      });
      if (result.error) {
        setError('Kunde inte skicka återställningslänk. Kontrollera e-postadressen.');
      } else {
        // In v1, show a copyable in-app link rather than sending email
        setResetLink(`${window.location.origin}/reset-password?token=check-your-server-logs`);
      }
    } catch {
      setError('Något gick fel. Försök igen.');
    } finally {
      setLoading(false);
    }
  };

  if (resetLink) {
    return (
      <AuthShell>
        <h1 className="text-2xl font-semibold tracking-tight">Återställningslänk</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Kopiera länken nedan och öppna den i en webbläsare:
        </p>
        <div className="mt-5 rounded-xl bg-muted p-3 text-xs break-all text-foreground select-all">
          {resetLink}
        </div>
        <p className="mt-3 text-xs text-muted-foreground">
          Tips: I produktionsmiljön konfigureras e-post via Mailcow/Stalwart.
        </p>
      </AuthShell>
    );
  }

  return (
    <AuthShell>
      <h1 className="text-2xl font-semibold tracking-tight">Glömt lösenordet?</h1>
      <p className="mt-1 text-sm text-muted-foreground">
        Ange din e-postadress så tar vi fram en återställningslänk.
      </p>
      <form onSubmit={handleSubmit} className="mt-6 flex flex-col gap-4">
        <div className="space-y-2">
          <Label htmlFor="forgot-email">E-postadress</Label>
          <Input
            id="forgot-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            autoComplete="email"
            className="h-10"
            placeholder="din@email.se"
          />
        </div>
        {error && (
          <p role="alert" className="rounded-lg bg-negative-soft px-3 py-2 text-sm text-negative">
            {error}
          </p>
        )}
        <Button type="submit" disabled={loading} className="mt-1 w-full" size="lg">
          {loading ? 'Skickar…' : 'Hämta återställningslänk'}
        </Button>
      </form>
    </AuthShell>
  );
}

function AuthShell({ children }: { children: ReactNode }) {
  return (
    <div className="flex min-h-svh flex-col items-center justify-center bg-background px-4 py-10">
      <div className="w-full max-w-sm">
        <div className="mb-6 flex justify-center">
          <BrandMark />
        </div>
        <div className="rounded-2xl border border-border bg-card p-6 sm:p-7">{children}</div>
        <p className="mt-5 text-center text-sm">
          <Link to="/login" className="font-medium text-primary hover:underline">
            Tillbaka till inloggning
          </Link>
        </p>
      </div>
    </div>
  );
}
